import { useState, useCallback, useEffect } from 'react';
import { Box, Flex, Text, Grid, SimpleGrid, chakra } from '@chakra-ui/react';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge, RecommendationBadge, RiskBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import type { AuditLogResponse, ScanSymbolSnapshot, SystemState } from '@market-buddy/shared';

type AuditEntry = AuditLogResponse['entries'][0];

type DatePreset = 'today' | 'yesterday' | '7days' | '';

function getDateRange(preset: DatePreset): { startDate?: string; endDate?: string } {
  if (!preset) return {};
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today':
      return { startDate: todayStart.toISOString() };
    case 'yesterday': {
      const yesterdayStart = new Date(todayStart);
      yesterdayStart.setDate(yesterdayStart.getDate() - 1);
      return { startDate: yesterdayStart.toISOString(), endDate: todayStart.toISOString() };
    }
    case '7days': {
      const weekAgo = new Date(todayStart);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { startDate: weekAgo.toISOString() };
    }
    default:
      return {};
  }
}

export function ActivityLog() {
  const { toast } = useToast();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [exitSummary, setExitSummary] = useState<AuditLogResponse['summary']>(undefined);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [expandedScanId, setExpandedScanId] = useState<string | null>(null);

  // Filters
  const [symbolFilter, setSymbolFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('');

  const pageSize = 25;

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (symbolFilter) params.set('symbol', symbolFilter);
      if (eventTypeFilter) params.set('eventType', eventTypeFilter);

      const range = getDateRange(datePreset);
      if (range.startDate) params.set('startDate', range.startDate);
      if (range.endDate) params.set('endDate', range.endDate);

      const data = await api.get<AuditLogResponse>(`/api/audit?${params}`);
      setEntries(data.entries);
      setTotal(data.total);
      setExitSummary(data.summary);
    } catch {
      if (loading) toast('Unable to load activity log', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, symbolFilter, eventTypeFilter, datePreset, loading, toast]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (symbolFilter) params.set('symbol', symbolFilter);
      if (eventTypeFilter) params.set('eventType', eventTypeFilter);

      const range = getDateRange(datePreset);
      if (range.startDate) params.set('startDate', range.startDate);
      if (range.endDate) params.set('endDate', range.endDate);

      const token = localStorage.getItem('mb-access-token');
      const response = await fetch(`/api/audit/export?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `market-buddy-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast('Activity log exported', 'success');
    } catch {
      toast('Unable to export. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  }

  function handleDatePreset(preset: DatePreset) {
    setDatePreset(preset === datePreset ? '' : preset);
    setPage(1);
  }

  const hasFilters = symbolFilter || eventTypeFilter || datePreset;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box>
      {/* Page header */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb="6"
      >
        <Text fontSize="xl" color="text.primary">Activity Log</Text>
        <Button variant="secondary" loading={exporting} onClick={handleExport}>
          Export CSV
        </Button>
      </Flex>

      {/* Cron Status */}
      <CronStatusPanel onTriggered={fetchEntries} />

      {/* Filters */}
      <Flex
        className="activity-filters"
        gap="3"
        mb="4"
        flexWrap="wrap"
        alignItems="center"
      >
        {/* Date quick filters */}
        <Flex className="date-filter-buttons" gap="1">
          {([['today', 'Today'], ['yesterday', 'Yesterday'], ['7days', '7 Days']] as const).map(([key, label]) => (
            <Button
              key={key}
              variant={datePreset === key ? 'primary' : 'ghost'}
              onClick={() => handleDatePreset(key)}
              style={{ fontSize: '13px', padding: '4px 12px', minHeight: '32px' }}
            >
              {label}
            </Button>
          ))}
        </Flex>

        <chakra.input
          type="text"
          placeholder="Filter by symbol..."
          value={symbolFilter}
          onChange={(e) => { setSymbolFilter(e.target.value); setPage(1); }}
          p="2 4"
          borderRadius="md"
          border="1px solid"
          borderColor="border.primary"
          bg="bg.primary"
          fontSize={{ base: '16px', md: '14px' }}
          width="180px"
          color="text.primary"
        />
        <chakra.select
          value={eventTypeFilter}
          onChange={(e) => { setEventTypeFilter(e.target.value); setPage(1); }}
          p="2 4"
          borderRadius="md"
          border="1px solid"
          borderColor="border.primary"
          bg="bg.primary"
          fontSize={{ base: '16px', md: '14px' }}
          color="text.primary"
        >
          <option value="">All types</option>
          <option value="recommendation">Recommendations</option>
          <option value="trade">Trades</option>
          <option value="exit">Exits only</option>
          <option value="auto_trade_gate">Trade Gates</option>
          <option value="circuit_breaker">Circuit Breaker</option>
          <option value="order_cleanup">Order Cleanup</option>
          <option value="watchlist">Watchlist Changes</option>
          <option value="scan">Cron Scans</option>
          <option value="daily_scan">Daily Scan</option>
          <option value="ai_update">AI Updates</option>
          <option value="alert">Alerts</option>
          <option value="insider_scan">Insider Scans</option>
          <option value="admin">Admin</option>
        </chakra.select>
        {hasFilters && (
          <Button variant="ghost" onClick={() => { setSymbolFilter(''); setEventTypeFilter(''); setDatePreset(''); setPage(1); }}>
            Clear filters
          </Button>
        )}
      </Flex>

      {/* Summary strip */}
      <Flex
        gap="6"
        mb="4"
        fontSize="14px"
        color="text.tertiary"
        flexWrap="wrap"
      >
        <span>Showing {total} entries</span>
        {eventTypeFilter === 'exit' && exitSummary && (
          <Flex gap="2" alignItems="center" data-testid="exit-summary">
            <Text as="span" color="text.secondary">
              {exitSummary.exits} exit{exitSummary.exits !== 1 ? 's' : ''}
            </Text>
            <Text as="span">&middot;</Text>
            <Text
              as="span"
              fontFamily="mono"
              color={exitSummary.netPnl >= 0 ? 'status.success.text' : 'status.error.text'}
            >
              net {exitSummary.netPnl >= 0 ? '+' : '-'}£{Math.abs(exitSummary.netPnl).toFixed(2)}
            </Text>
            <Text as="span">&middot;</Text>
            <Text as="span" color="text.secondary">
              {exitSummary.wins} win{exitSummary.wins !== 1 ? 's' : ''} / {exitSummary.losses} loss{exitSummary.losses !== 1 ? 'es' : ''}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Table */}
      <Card>
        {loading ? (
          <SkeletonRows count={10} height={64} />
        ) : entries.length === 0 ? (
          <Box
            p="16 4"
            textAlign="center"
          >
            <Text fontSize="lg" color="text.primary" mb="2">
              {hasFilters ? 'No entries match the current filters' : 'No activity yet'}
            </Text>
            <Text fontSize="sm" color="text.tertiary">
              {hasFilters
                ? 'Try adjusting your filters.'
                : 'Your recommendations, approvals, and alerts will appear here as you use Market Buddy.'}
            </Text>
          </Box>
        ) : (
          <>
          {entries.map((entry) => {
            const isScan = entry.eventType === 'scan';
            const meta = isScan ? parseScanMetadata(entry.metadata) : null;
            const hasSnapshots = meta !== null && meta.snapshots.length > 0;
            const isExpanded = expandedScanId === entry.id;

            return (
              <Box key={entry.id}>
                {/* Scrollable row */}
                <Box className="activity-table-wrap">
                  {/* Table header — only before first entry */}
                  {entry === entries[0] && (
                    <Grid
                      className="activity-table-header"
                      templateColumns="100px 100px 120px 1fr 150px"
                      gap="3"
                      p="3 4"
                      borderBottom="1px solid"
                      borderColor="border.secondary"
                      fontSize="12px"
                      fontWeight="500"
                      color="text.tertiary"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                    >
                      <span>Time</span>
                      <span>Instrument</span>
                      <span>Type</span>
                      <span>Description</span>
                      <span>Outcome</span>
                    </Grid>
                  )}
                  {isScan ? (
                    <ScanCardHeader
                      entry={entry}
                      meta={meta}
                      hasSnapshots={hasSnapshots}
                      expanded={isExpanded}
                      onToggle={() => setExpandedScanId(isExpanded ? null : entry.id)}
                    />
                  ) : (
                    <ActivityRow entry={entry} />
                  )}
                </Box>

                {/* Expanded detail — OUTSIDE scroll container */}
                {isScan && isExpanded && hasSnapshots && (
                  <Box className="scan-detail-grid slide-down" p="2 4 4 4">
                    {meta!.failedSymbols.length > 0 && (
                      <Box mb="3" p="2 3" borderRadius="md" bg="bg.error" fontSize="13px" color="text.warning">
                        {meta!.failedSymbols.map((f) => (
                          <Text key={f.symbol}><strong>{f.symbol}</strong>: {f.error}</Text>
                        ))}
                      </Box>
                    )}
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap="3">
                      {meta!.snapshots.map((snap) => (
                        <SnapshotMiniCard key={snap.symbol} snapshot={snap} />
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </Box>
            );
          })}
          </>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex
          alignItems="center"
          justifyContent="center"
          gap="4"
          mt="4"
          fontSize="14px"
          color="text.secondary"
        >
          <Button
            variant="ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span>Page {page} of {totalPages}</span>
          <Button
            variant="ghost"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </Flex>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ActivityRow — non-scan entries
// ---------------------------------------------------------------------------

function ActivityRow({ entry }: { entry: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const meta = parseTradeMetadata(entry.metadata);
  const isTrade = entry.eventType === 'trade';
  const isExit = entry.eventType === 'exit';
  const isGate = entry.eventType === 'auto_trade_gate' || entry.eventType === 'circuit_breaker';

  return (
    <Box>
      <Grid
        className="activity-row"
        templateColumns="100px 100px 120px 1fr 150px"
        gap="3"
        p="3 4"
        borderBottom={expanded ? 'none' : '1px solid'}
        borderColor="border.secondary"
        minHeight="64px"
        alignItems="center"
        overflow="hidden"
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        _hover={{ bg: 'bg.secondary' }}
        transition="background-color 150ms ease"
      >
        <Box>
          <Text fontSize="sm" fontFamily="mono" color="text.primary">
            {new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </Text>
          <Text fontSize="xs" color="text.tertiary">
            {new Date(entry.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </Text>
        </Box>
        <Text fontSize="sm" fontWeight="600" color="text.primary">
          {entry.symbol}
        </Text>
        <EventBadge type={entry.eventType} />
        <Box minWidth="0">
          {entry.recommendation && (
            <Flex alignItems="center" gap="2" mb="0.5">
              <RecommendationBadge recommendation={entry.recommendation} />
              {entry.confidence && (
                <Text fontSize="xs" color="text.tertiary">
                  {entry.confidence} confidence
                </Text>
              )}
            </Flex>
          )}
          {entry.rationale && (
            <Text
              fontSize="sm"
              color="text.secondary"
              overflow="hidden"
              textOverflow={expanded ? undefined : 'ellipsis'}
              whiteSpace={expanded ? 'normal' : 'nowrap'}
            >
              {entry.rationale}
            </Text>
          )}
        </Box>
        {isExit ? (
          <Flex direction="column" alignItems="flex-start" gap="1">
            <ExitActionBadge action={entry.userAction} />
            {typeof entry.pnl === 'number' && (
              <PnlChip pnl={entry.pnl} pnlPercent={entry.pnlPercent} />
            )}
          </Flex>
        ) : isTrade ? (
          <Badge variant="neutral">
            {typeof meta?.action === 'string' && meta.action.toLowerCase() === 'sell' ? 'Sell' : 'Buy'}
          </Badge>
        ) : (
          <OutcomeBadge action={entry.userAction} />
        )}
      </Grid>

      {/* Expanded detail panel */}
      {expanded && (entry.metadata || isTrade || isGate) && (
        <Box
          p="3 4"
          bg="bg.secondary"
          borderBottom="1px solid"
          borderColor="border.secondary"
          fontSize="13px"
        >
          {isTrade && meta && (
            <Flex gap="6" flexWrap="wrap">
              {meta.action && (
                <DetailItem label="Action" value={meta.action.toUpperCase()} />
              )}
              {meta.ticker && (
                <DetailItem label="Ticker" value={meta.ticker} />
              )}
              {meta.quantity && (
                <DetailItem label="Qty" value={String(Math.abs(meta.quantity))} />
              )}
              {meta.stopLoss && (
                <DetailItem label="Stop-Loss" value={`$${meta.stopLoss.toFixed(2)}`} />
              )}
              {meta.takeProfit && (
                <DetailItem label="Take-Profit" value={`$${meta.takeProfit.toFixed(2)}`} />
              )}
              {meta.riskBand && (
                <DetailItem label="Risk" value={meta.riskBand} />
              )}
              {meta.orderId && (
                <DetailItem label="Order ID" value={meta.orderId} />
              )}
              {meta.stopOrderId && (
                <DetailItem label="Stop Order" value={meta.stopOrderId} />
              )}
              {meta.tpOrderId && (
                <DetailItem label="TP Order" value={meta.tpOrderId} />
              )}
              {meta.conviction && (
                <DetailItem label="Conviction" value={`${meta.conviction}/100`} />
              )}
            </Flex>
          )}
          {isExit && meta && (
            <Flex gap="6" flexWrap="wrap">
              {typeof meta.ticker === 'string' && (
                <DetailItem label="Ticker" value={meta.ticker} />
              )}
              {typeof meta.quantity === 'number' && (
                <DetailItem label="Qty" value={String(Math.abs(meta.quantity))} />
              )}
              {typeof meta.entryPrice === 'number' && (
                <DetailItem label="Entry" value={meta.entryPrice.toFixed(2)} />
              )}
              {typeof meta.exitPrice === 'number' && (
                <DetailItem
                  label="Exit"
                  value={`${meta.exitPrice.toFixed(2)}${meta.estimated ? ' (est.)' : ''}`}
                />
              )}
              {typeof entry.pnl === 'number' && (
                <DetailItem
                  label="Realised P&L"
                  value={`${entry.pnl >= 0 ? '+' : '-'}£${Math.abs(entry.pnl).toFixed(2)}${
                    typeof entry.pnlPercent === 'number'
                      ? ` (${entry.pnlPercent >= 0 ? '+' : ''}${entry.pnlPercent.toFixed(2)}%)`
                      : ''
                  }`}
                />
              )}
            </Flex>
          )}
          {!isTrade && !isExit && entry.metadata && (
            <Text fontSize="12px" fontFamily="mono" color="text.tertiary" whiteSpace="pre-wrap" wordBreak="break-all">
              {formatMetadataJson(entry.metadata)}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="11px" color="text.tertiary" textTransform="uppercase" letterSpacing="0.04em">
        {label}
      </Text>
      <Text fontSize="13px" fontFamily="mono" color="text.primary" fontWeight={500}>
        {value}
      </Text>
    </Box>
  );
}

function parseTradeMetadata(raw?: string): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function formatMetadataJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// ---------------------------------------------------------------------------
// ScanCardHeader — row header for scan entries (detail rendered by parent)
// ---------------------------------------------------------------------------

interface ScanMeta {
  groupSize: number;
  succeeded: number;
  failed: number;
  failedSymbols: Array<{ symbol: string; error: string }>;
  snapshots: ScanSymbolSnapshot[];
}

function ScanCardHeader({ entry, meta, hasSnapshots, expanded, onToggle }: {
  entry: AuditEntry;
  meta: ScanMeta | null;
  hasSnapshots: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Grid
      className="activity-row"
      templateColumns="100px 100px 120px 1fr 150px"
      gap="3"
      p="3 4"
      minHeight="64px"
      alignItems="center"
      borderBottom="1px solid"
      borderColor="border.secondary"
      opacity={0.7}
      cursor={hasSnapshots ? 'pointer' : 'default'}
      onClick={() => hasSnapshots && onToggle()}
      _hover={hasSnapshots ? { bg: 'bg.secondary' } : undefined}
      transition="background-color 150ms ease"
    >
      <Box>
        <Text fontSize="sm" fontFamily="mono" color="text.primary">
          {new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </Text>
        <Text fontSize="xs" color="text.tertiary">
          {new Date(entry.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
        </Text>
      </Box>
      <Text fontSize="sm" fontWeight="600" color="text.primary">
        {meta ? `${meta.groupSize} checked` : entry.symbol}
      </Text>
      <EventBadge type={entry.eventType} />
      <Box minWidth="0">
        {entry.rationale && (
          <Text
            fontSize="sm"
            color="text.secondary"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {entry.rationale}
          </Text>
        )}
      </Box>
      <Flex alignItems="center" gap="2">
        {meta ? (
          <Text fontSize="sm" color={meta.failed > 0 ? 'text.warning' : 'text.tertiary'}>
            {meta.failed > 0 ? `${meta.failed} failed` : 'OK'}
          </Text>
        ) : (
          <Text fontSize="sm" color="text.tertiary">--</Text>
        )}
        {hasSnapshots && (
          <Text
            fontSize="xs"
            color="text.tertiary"
            transition="transform 200ms ease"
            transform={expanded ? 'rotate(180deg)' : 'rotate(0deg)'}
          >
            &#9660;
          </Text>
        )}
      </Flex>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// SnapshotMiniCard
// ---------------------------------------------------------------------------

const regimeBadgeMap: Record<string, 'brand' | 'warning' | 'neutral'> = {
  trending_up: 'brand',
  trending_down: 'brand',
  volatile: 'warning',
  ranging: 'neutral',
  dead: 'neutral',
};

function SnapshotMiniCard({ snapshot }: { snapshot: ScanSymbolSnapshot }) {
  const changeColor = snapshot.changePercent >= 0 ? 'status.success.text' : 'status.error.text';
  const riskLevel = (snapshot.riskBand || 'moderate') as 'low' | 'moderate' | 'elevated' | 'high';
  const regimeVariant = regimeBadgeMap[snapshot.regime] ?? 'neutral';
  const regimeLabel = snapshot.regime.replace(/_/g, ' ');

  return (
    <Box
      p="3"
      borderRadius="md"
      border="1px solid"
      borderColor="border.secondary"
      bg="bg.primary"
    >
      <Flex justifyContent="space-between" alignItems="center" mb="2">
        <Text fontSize="sm" fontWeight="700" color="text.primary">
          {snapshot.symbol}
        </Text>
        {snapshot.success && (
          <Badge variant={regimeVariant}>
            {regimeLabel}
          </Badge>
        )}
      </Flex>
      {snapshot.success ? (
        <>
          <Flex alignItems="baseline" gap="2" mb="1.5">
            <Text fontSize="sm" fontFamily="mono" color="text.primary">
              {snapshot.price > 0 ? snapshot.price.toFixed(snapshot.price < 10 ? 4 : 2) : '--'}
            </Text>
            <Text fontSize="xs" fontFamily="mono" color={changeColor}>
              {snapshot.changePercent >= 0 ? '+' : ''}{snapshot.changePercent.toFixed(2)}%
            </Text>
          </Flex>
          <RiskBadge level={riskLevel} />
        </>
      ) : (
        <Text fontSize="xs" color="text.tertiary">No data</Text>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CronStatusPanel
// ---------------------------------------------------------------------------

interface AdminStatusResponse {
  system?: SystemState;
}

function CronStatusPanel({ onTriggered }: { onTriggered: () => void }) {
  const { toast } = useToast();
  const [system, setSystem] = useState<SystemState | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      // Same consolidated truth as the banner and Settings — durable last-run
      // from audit_log and next runs from the REAL cron windows.
      const data = await api.get<AdminStatusResponse>('/api/admin/status');
      if (data.system) setSystem(data.system);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  async function handleTrigger() {
    setTriggering(true);
    try {
      const result = await api.post<{ success: boolean; symbols: string[]; results: Array<{ symbol: string; success: boolean }> }>('/api/cron/trigger', {});
      const succeeded = result.results?.filter((r) => r.success).length ?? 0;
      toast(`Run triggered: ${succeeded}/${result.symbols?.length ?? 0} symbols processed`, 'success');
      fetchStatus();
      onTriggered();
    } catch {
      toast('Failed to trigger cron run', 'error');
    } finally {
      setTriggering(false);
    }
  }

  function relativeTime(ts: number): string {
    const diff = Math.abs(Date.now() - ts);
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ${hrs % 24}h ago`;
  }

  /** "in 25m (14:35 UTC)" for near runs; "Mon 14:00 UTC" once it's hours away. */
  function formatNextRun(ts: number): string {
    const diffMins = Math.ceil((ts - Date.now()) / 60_000);
    const time = new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    if (diffMins <= 0) return 'due now';
    if (diffMins < 90) return `in ${diffMins}m (${time} UTC)`;
    const day = new Date(ts).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
    return `${day} ${time} UTC`;
  }

  if (loading) return null;
  if (!system) return null;

  const inWindow = system.marketState === 'open';
  const statusBadge = !system.pollingEnabled
    ? { variant: 'warning' as const, label: 'Paused' }
    : inWindow
      ? { variant: 'success' as const, label: 'Active' }
      : { variant: 'neutral' as const, label: system.marketState === 'weekend' ? 'Idle (market closed — weekend)' : 'Idle (market closed)' };

  return (
    <Card mb="4" style={{ padding: '16px 20px' }}>
      <CardHeader style={{ marginBottom: '12px' }}>
        <Box>
          <Flex alignItems="center" gap="2">
            <Text fontSize="sm" fontWeight="600" color="text.primary">Background Monitor</Text>
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
          </Flex>
          <Text fontSize="sm" color="text.secondary" mt="1">
            Scans your watchlist every 5 minutes, Mon-Fri 14:00-16:55 and 19:00-20:55 UTC.
          </Text>
        </Box>
      </CardHeader>
      <CardBody>
        <Grid templateColumns="repeat(4, 1fr)" gap="4">
          <Box>
            <Text fontSize="xs" color="text.tertiary" mb="0.5">Last run</Text>
            <Text fontSize="sm" fontFamily="mono" color="text.primary">
              {system.lastCronRun ? relativeTime(system.lastCronRun) : 'No runs recorded'}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="text.tertiary" mb="0.5">
              {system.pollingEnabled ? 'Next run' : 'Next run (paused)'}
            </Text>
            <Text fontSize="sm" fontFamily="mono" color="text.primary">
              {formatNextRun(system.nextCronRun)}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="text.tertiary" mb="0.5">Symbols</Text>
            <Text fontSize="sm" fontFamily="mono" color="text.primary">
              {system.totalSymbols} on watchlist
            </Text>
          </Box>
          <Flex alignItems="flex-end" justifyContent="flex-end">
            <Button variant="secondary" loading={triggering} onClick={handleTrigger}>
              Run now
            </Button>
          </Flex>
        </Grid>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseScanMetadata(raw?: string): ScanMeta | null {
  if (!raw) return null;
  try {
    const m = JSON.parse(raw);
    return {
      groupSize: m.groupSize ?? 0,
      succeeded: m.succeeded ?? 0,
      failed: m.failed ?? 0,
      failedSymbols: Array.isArray(m.failedSymbols) ? m.failedSymbols : [],
      snapshots: Array.isArray(m.snapshots) ? m.snapshots : [],
    };
  } catch {
    return null;
  }
}

function EventBadge({ type }: { type: string }) {
  const map: Record<string, { variant: 'brand' | 'neutral' | 'warning' | 'success' | 'error'; label: string }> = {
    recommendation: { variant: 'brand', label: 'Signal' },
    ai_update: { variant: 'neutral', label: 'AI Update' },
    alert: { variant: 'warning', label: 'Alert' },
    trade: { variant: 'success', label: 'Trade' },
    exit: { variant: 'neutral', label: 'Exit' },
    auto_trade_gate: { variant: 'warning', label: 'Gate Block' },
    circuit_breaker: { variant: 'error', label: 'Circuit Breaker' },
    order_cleanup: { variant: 'neutral', label: 'Order Cleanup' },
    watchlist: { variant: 'brand', label: 'Watchlist' },
    insider_scan: { variant: 'neutral', label: 'Insider Scan' },
    edgar_scan: { variant: 'neutral', label: 'Insider Scan' },
    scan: { variant: 'neutral', label: 'Cron Scan' },
    daily_scan: { variant: 'brand', label: 'Daily Scan' },
    admin: { variant: 'warning', label: 'Admin' },
  };
  const config = map[type] ?? { variant: 'neutral' as const, label: type.replace(/_/g, ' ') };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

/** Per-exit-type badge: plain-language label + colour for position closes. */
function ExitActionBadge({ action }: { action?: string }) {
  if (!action) return <Text as="span" color="text.tertiary">--</Text>;

  const map: Record<string, { variant: 'success' | 'neutral' | 'warning' | 'error' | 'brand'; label: string }> = {
    stop_exit: { variant: 'warning', label: 'Stop hit' },
    force_exit_max_loss: { variant: 'error', label: 'Max-loss backstop' },
    force_exit_max_hold: { variant: 'warning', label: 'Time-limit exit' },
    quick_profit_exit: { variant: 'success', label: 'Quick profit' },
    rotation_exit: { variant: 'neutral', label: 'Rotation' },
    tp_exit: { variant: 'success', label: 'Target hit' },
    tp1_partial: { variant: 'success', label: 'Partial profit' },
    engine_exit: { variant: 'neutral', label: 'Engine exit' },
    engine_reduce: { variant: 'neutral', label: 'Engine reduce' },
    admin_close: { variant: 'neutral', label: 'Manual close' },
  };
  const config = map[action] ?? { variant: 'neutral' as const, label: action.replace(/_/g, ' ') };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

/** Signed realised-P&L chip: +£x.xx green / -£x.xx red, with percentage. */
function PnlChip({ pnl, pnlPercent }: { pnl: number; pnlPercent?: number }) {
  const positive = pnl >= 0;
  return (
    <Text
      as="span"
      fontSize="xs"
      fontFamily="mono"
      fontWeight="600"
      color={positive ? 'status.success.text' : 'status.error.text'}
    >
      {positive ? '+' : '-'}£{Math.abs(pnl).toFixed(2)}
      {typeof pnlPercent === 'number' && (
        <> ({pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}%)</>
      )}
    </Text>
  );
}

function OutcomeBadge({ action }: { action?: string }) {
  if (!action) return <Text as="span" color="text.tertiary">--</Text>;

  const map: Record<string, { variant: 'success' | 'neutral' | 'warning' | 'error' | 'brand'; label: string }> = {
    approved: { variant: 'success', label: 'Approved' },
    dismissed: { variant: 'neutral', label: 'Dismissed' },
    expired: { variant: 'neutral', label: 'Expired' },
    auto_trade: { variant: 'success', label: 'Auto-Traded' },
    manual_trade: { variant: 'success', label: 'Manual Trade' },
    edgar_auto_buy: { variant: 'success', label: 'EDGAR Buy' },
    edgar_auto_exit: { variant: 'warning', label: 'EDGAR Exit' },
    auto_add: { variant: 'brand', label: 'Auto-Added' },
    auto_remove: { variant: 'warning', label: 'Auto-Removed' },
    auto_trade_disabled: { variant: 'error', label: 'Trading Killed' },
    orphan_cleanup: { variant: 'neutral', label: 'Cleaned Up' },
    scan_complete: { variant: 'neutral', label: 'Complete' },
    daily_scan: { variant: 'neutral', label: 'Complete' },
    reset: { variant: 'warning', label: 'Reset' },
  };
  const config = map[action] ?? { variant: 'neutral' as const, label: action.replace(/_/g, ' ') };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}
